import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

/**
 * Hook for persisting tab state in URL query parameters
 * This ensures tabs remember their state across page refreshes
 * 
 * @param defaultTab - The default tab key/index to use if no URL param exists
 * @param tabMapping - Optional mapping of URL param values to internal tab keys (for string tabs)
 * @returns [activeTab, setActiveTab] - Current tab state and setter function
 * 
 * @example
 * // For numeric tabs (Material-UI)
 * const [activeTab, setActiveTab] = useTabPersistence(0);
 * 
 * @example
 * // For string tabs (Ant Design)
 * const [activeTab, setActiveTab] = useTabPersistence('overview');
 * 
 * @example
 * // With custom mapping
 * const [activeTab, setActiveTab] = useTabPersistence('platform', {
 *   'platform': 'platform-connectors',
 *   'enterprise': 'enterprise-connectors'
 * });
 */
export function useTabPersistence<T extends string | number>(
  defaultTab: T,
  tabMapping?: Record<string, T>
): [T, (tab: T) => void] {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTabState] = useState<T>(defaultTab);

  // Read tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    
    if (tabParam) {
      // If we have a mapping, use it to convert URL param to internal tab key
      if (tabMapping && tabMapping[tabParam] !== undefined) {
        setActiveTabState(tabMapping[tabParam]);
      } else if (typeof defaultTab === 'number') {
        // For numeric tabs, parse the param as a number
        const numericTab = parseInt(tabParam, 10);
        if (!isNaN(numericTab)) {
          setActiveTabState(numericTab as T);
        }
      } else {
        // For string tabs, use the param directly
        setActiveTabState(tabParam as T);
      }
    }
  }, [searchParams, tabMapping, defaultTab]);

  // Function to update both state and URL
  const setActiveTab = (tab: T) => {
    setActiveTabState(tab);
    
    // Update URL to reflect the new tab
    const currentUrl = new URL(window.location.href);
    const newSearchParams = new URLSearchParams(currentUrl.search);
    
    // Convert tab to URL param value
    let tabParamValue: string;
    
    if (tabMapping) {
      // If we have a reverse mapping, find the URL param value for this tab
      const reverseMapping = Object.entries(tabMapping).reduce((acc, [key, value]) => {
        acc[String(value)] = key;
        return acc;
      }, {} as Record<string, string>);
      
      tabParamValue = reverseMapping[String(tab)] || String(tab);
    } else {
      tabParamValue = String(tab);
    }
    
    newSearchParams.set('tab', tabParamValue);
    const next = `${currentUrl.pathname}?${newSearchParams.toString()}`;
    const current = `${currentUrl.pathname}${currentUrl.search}`;
    if (next !== current) {
      navigate(next, { replace: true });
    }
  };

  return [activeTab, setActiveTab];
}

/**
 * Hook for persisting Material-UI numeric tabs with friendly URL names
 * 
 * @param defaultTabIndex - The default tab index (0-based)
 * @param tabNames - Array of friendly names for each tab (e.g., ['overview', 'details', 'settings'])
 * @returns [activeTab, setActiveTab] - Current tab index and setter function
 * 
 * @example
 * const [activeTab, setActiveTab] = useNumericTabPersistence(0, ['overview', 'details', 'settings']);
 */
export function useNumericTabPersistence(
  defaultTabIndex: number,
  tabNames: string[]
): [number, (index: number) => void] {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTabState] = useState<number>(defaultTabIndex);

  // Read tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    
    if (tabParam) {
      // Try to find the index of the tab name
      const tabIndex = tabNames.indexOf(tabParam);
      if (tabIndex !== -1) {
        setActiveTabState(tabIndex);
      } else {
        // Try to parse as a number
        const numericTab = parseInt(tabParam, 10);
        if (!isNaN(numericTab) && numericTab >= 0 && numericTab < tabNames.length) {
          setActiveTabState(numericTab);
        }
      }
    }
  }, [searchParams, tabNames]);

  // Function to update both state and URL
  const setActiveTab = (index: number) => {
    setActiveTabState(index);
    
    // Update URL with friendly tab name
    const currentUrl = new URL(window.location.href);
    const newSearchParams = new URLSearchParams(currentUrl.search);
    
    if (index >= 0 && index < tabNames.length) {
      newSearchParams.set('tab', tabNames[index]);
      navigate(`${currentUrl.pathname}?${newSearchParams.toString()}`, { replace: true });
    }
  };

  return [activeTab, setActiveTab];
}




